import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import DataTable from "@/components/organisms/DataTable";
import { ColumnDef, Row } from "@tanstack/react-table";
import TabSheet, { OnSubmitProps } from "./TabSheet";
import type FormFieldItem from "@/types/Form/FormFieldItem";

export type SetOpen = (v: boolean) => void;
export type SetData = (v: any) => void;

type TabTableProps<T> = {
  columns: (setOpen: SetOpen, setData: SetData) => ColumnDef<T>[];
  data: T[];
  form: UseFormReturn<any, any, any>;
  formFields: FormFieldItem<T>[];
  edit?: boolean;
  onSubmit: OnSubmitProps<T>;
  setEdit?: (edit: boolean) => void;
  renderSubComponent?: (
    props: {
      row: Row<T>;
    },
    setOpen: (v: boolean) => void,
    setData: (v: T | null) => void
  ) => React.ReactNode;
  currentPage?: number;
  pageSize?: number;
  isDifferentTableColumns?: boolean;
  sheetSide?: "left" | "right" | "bottom" | "top";
  loading?: boolean;
  baseError?: string | null;
  showSwitch?: boolean;
  onUnfreezeSowChange?: (value: boolean) => void;
  formClass?: string;
};

const TabTable = <T extends unknown>({
  columns,
  data,
  form,
  formFields,
  edit = false,
  onSubmit,
  setEdit = () => {},
  renderSubComponent,
  currentPage = 1,
  pageSize = 10,
  isDifferentTableColumns = false,
  sheetSide= "right",
  loading = false,
  baseError = null,
  showSwitch = false,
  onUnfreezeSowChange = () => {},
  formClass = "space-y-8",
}: TabTableProps<T>) => {
  const [sheetOpen, setSheetOpen] = useState<boolean>(false);
  const [sheetData, setSheetData] = useState<T | null>(null);

  useEffect(() => {
    form.reset(sheetData);
  }, [sheetData, sheetOpen]);

  return (
    <div>
      <DataTable<T, T>
        columns={columns(setSheetOpen, setSheetData)}
        data={data}
        pageSize={pageSize}
        currentPage={currentPage}
        isDifferentTableColumns={isDifferentTableColumns}
        {...(renderSubComponent && {
          renderSubComponent: ({ row }) =>
            renderSubComponent(
              { row } as { row: Row<T> },
              setSheetOpen,
              setSheetData
            ),
        })}
        
      />
      {sheetData && (
        <TabSheet
          form={form}
          edit={edit}
          data={sheetData}
          open={sheetOpen}
          formClass={formClass}
          formFields={formFields}
          onOpen={setSheetOpen}
          setData={setSheetData}
          setEdit={setEdit}
          onSubmit={onSubmit}
          side={sheetSide}
          loading={loading}
          baseError={baseError}
          showSwitch={showSwitch}
          onUnfreezeSowChange={onUnfreezeSowChange}
        />
      )}
    </div>
  );
};

export default TabTable;
